<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeApi, useStoresStore } from '@/entities/store'
import ozonLogo from '@/shared/assets/svg/ozon-logo.svg'
import wildberriesLogo from '@/shared/assets/svg/wildberries-logo.svg'
import { setCookie } from '@/shared/lib/utils/cookies'
import CButton from '@/shared/ui/CButton.vue'
import CDialog from '@/shared/ui/CDialog.vue'
import CInfoBox from '@/shared/ui/CInfoBox.vue'
import CTab from '@/shared/ui/CTab.vue'

const emit = defineEmits<Emit>()

const { t } = useI18n()

const model = defineModel<boolean>()
const isLoading = ref(false)

// Define emits for communication with parent
interface Emit {
  (event: 'storeCreated', storeId: string): void
}

// Get stores store for refetching
const storesStore = useStoresStore()

// Active tab
const activeTab = ref('wildberries')

// Tabs configuration
const tabsConfig = computed(() => [
  {
    label: 'Wildberries',
    value: 'wildberries',
    image: wildberriesLogo,
  },
  {
    label: 'Ozon',
    value: 'ozon',
    image: ozonLogo,
  },
])

const form = ref({
  wildberries: {
    storeName: '',
    apiKey: '',
  },
  ozon: {
    storeName: '',
    apiKey: '',
  },
})

const isFormValid = computed(() => {
  const currentForm = form.value[activeTab.value as keyof typeof form.value]
  return currentForm.storeName.trim() !== '' && currentForm.apiKey.trim() !== ''
})

async function onSave() {
  if (isFormValid.value) {
    isLoading.value = true
    try {
      const currentForm = form.value[activeTab.value as keyof typeof form.value]

      // Prepare the data for the API
      const storeData = {
        name: currentForm.storeName,
        api_key: currentForm.apiKey,
      }

      // Make the API call
      const response = await storeApi.createStore(storeData)

      // Save the new store ID to cookies
      if (response.id) {
        setCookie('storeId', response.id.toString(), {
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
          path: '/',
          secure: true,
          sameSite: 'strict',
        })
      }

      // Refetch stores list to include the new store
      await storesStore.refetch()

      // Emit store created event to parent
      emit('storeCreated', response.id.toString())

      // Close dialog and reset form
      model.value = false
      form.value = {
        wildberries: { storeName: '', apiKey: '' },
        ozon: { storeName: '', apiKey: '' },
      }
      activeTab.value = 'wildberries'
    }
    catch (error) {
      console.error('Failed to create store:', error)
      // You might want to show an error message to the user here
    }
    finally {
      isLoading.value = false
    }
  }
}
</script>

<template>
  <Teleport to="body">
    <CDialog v-model="model" class="sm:!w-[480px] !w-[90%]">
      <template #header>
        <div class="flex justify-between items-center w-full">
          <h3 class="text-lg font-semibold text-text-primary">
            {{ t("add_store") }}
          </h3>
          <button
            class="text-gray-400 hover:text-gray-600 transition-200"
            @click="model = false"
          >
            <i class="icon-close text-xl" />
          </button>
        </div>
      </template>

      <div class="space-y-6">
        <!-- Information Box -->
        <CInfoBox :text="t('store_connection_info')" />

        <!-- Marketplace Selection Title -->
        <div v-if="false" class="space-y-2">
          <h4 class="text-xs font-medium text-text-primary">
            {{ t("select_marketplace") }}
          </h4>

          <!-- Tabs -->
          <CTab v-model="activeTab" :tabs="tabsConfig" />
        </div>

        <!-- Tab Content -->
        <div class="space-y-4">
          <!-- Wildberries Tab -->
          <template v-if="activeTab === 'wildberries'">
            <div class="space-y-3">
              <div>
                <label class="block text-xs font-medium text-text-primary mb-2">
                  {{ t("store_name") }}
                </label>
                <ElInput
                  v-model="form.wildberries.storeName"
                  :placeholder="t('enter_store_name')"
                  class="w-full"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-text-primary mb-2">
                  {{ t("api_key") }}
                </label>
                <ElInput
                  v-model="form.wildberries.apiKey"
                  :placeholder="t('enter_api_key')"
                  class="w-full"
                />
              </div>
            </div>
          </template>

          <!-- Ozon Tab -->
          <template v-if="activeTab === 'ozon'">
            <div class="space-y-3">
              <div>
                <label class="block text-xs font-medium text-text-primary mb-2">
                  {{ t("store_name") }}
                </label>
                <ElInput
                  v-model="form.ozon.storeName"
                  :placeholder="t('enter_store_name')"
                  class="w-full"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-text-primary mb-2">
                  {{ t("api_key") }}
                </label>
                <ElInput
                  v-model="form.ozon.apiKey"
                  :placeholder="t('enter_api_key')"
                  class="w-full"
                />
              </div>
            </div>
          </template>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <CButton type="default" @click="model = false">
            {{ t("cancel") }}
          </CButton>
          <CButton
            type="primary"
            :disabled="!isFormValid || isLoading"
            :loading="isLoading"
            @click="onSave"
          >
            <template #loading>
              {{ t("saving") }}
            </template>
            <template #default>
              {{ t("save") }} <i class="icon-tick text-2xl" />
            </template>
          </CButton>
        </div>
      </template>
    </CDialog>
  </Teleport>
</template>

<style scoped>
/* Dialog specific tab styles */
</style>
